import { type UserHistoryDto } from "@crowdcoursing/api/openapi";
import { Clear } from "@mui/icons-material";
import { Box, type BoxProps, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { useMemo, useState } from "react";

import { Route } from "~services/router/Route";
import { makeEventAvatarUrl, makeRoomAvatarUrl } from "~utils/url";

import { HistoryItemCard } from "./HistoryItemCard";

const tNoRoomsOrEvents = "No rooms or events";
const tSearchPlaceholder = "Search rooms and events...";
const tNoResults = "No results found";

function Container(props: BoxProps) {
  return <Box {...props}>{props.children}</Box>;
}

interface CombinedItem {
  id: string;
  name: string;
  description?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  avatarUrl: string;
  linkTo: string;
  type: "Room" | "Event";
}

export interface UserHistoryRoomsAndEventsBaseProps {
  rooms: NonNullable<UserHistoryDto["rooms"]>;
  events: NonNullable<UserHistoryDto["events"]>;
}
export interface UserHistoryRoomsAndEventsSlotProps {
  slotProps?: { container?: BoxProps };
}
export type UserHistoryRoomsAndEventsProps = UserHistoryRoomsAndEventsBaseProps & UserHistoryRoomsAndEventsSlotProps;

export const UserHistoryRoomsAndEvents = ({ rooms, events, slotProps }: UserHistoryRoomsAndEventsProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const combinedItems = useMemo(() => {
    const roomItems: CombinedItem[] = rooms.map((room) => ({
      avatarUrl: makeRoomAvatarUrl(room.id ?? ""),
      description: room.description ?? undefined,
      id: room.id ?? "",
      linkTo: Route.roomView({ roomId: room.id ?? "NO_ROOM_ID" }),
      name: room.name ?? "",
      ownerFirstName: room.ownerFirstName ?? undefined,
      ownerLastName: room.ownerLastName ?? undefined,
      type: "Room" as const,
    }));

    const eventItems: CombinedItem[] = events.map((event) => ({
      avatarUrl: makeEventAvatarUrl(event.id ?? ""),
      description: event.description ?? undefined,
      id: event.id ?? "",
      linkTo: Route.eventView({ eventId: event.id ?? "NO_EVENT_ID" }),
      name: event.name ?? "",
      ownerFirstName: event.ownerFirstName ?? undefined,
      ownerLastName: event.ownerLastName ?? undefined,
      type: "Event" as const,
    }));

    // Combine and sort alphabetically by name
    return [...roomItems, ...eventItems].sort((a, b) => a.name.localeCompare(b.name));
  }, [rooms, events]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return combinedItems;
    }

    const query = searchQuery.toLowerCase();
    return combinedItems.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.type.toLowerCase().includes(query) ||
        `${item.ownerFirstName} ${item.ownerLastName}`.toLowerCase().includes(query),
    );
  }, [combinedItems, searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  if (combinedItems.length === 0) {
    return (
      <Container {...slotProps?.container}>
        <Typography sx={{ fontStyle: "italic" }}>{tNoRoomsOrEvents}</Typography>
      </Container>
    );
  }

  return (
    <Container {...slotProps?.container}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minWidth: 0,
          width: "100%",
        }}
      >
        <TextField
          fullWidth
          placeholder={tSearchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          slotProps={{
            input: {
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search" onClick={handleClearSearch} edge="end" size="small">
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 1 }}
        />

        {filteredItems.length === 0 ? (
          <Box sx={{ padding: 2, textAlign: "center" }}>
            <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
              {tNoResults}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {filteredItems.map((item) => (
              <Box key={`${item.type}-${item.id}`} sx={{ minWidth: 0, width: "100%" }}>
                <HistoryItemCard
                  id={item.id}
                  name={item.name}
                  description={item.description}
                  ownerFirstName={item.ownerFirstName}
                  ownerLastName={item.ownerLastName}
                  avatarUrl={item.avatarUrl}
                  linkTo={item.linkTo}
                  type={item.type}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};
